import React, { useState } from 'react';
import { UserProfile, NotificationMode, UserNotificationPreferences } from '../../types';
import { BellRing, BellOff, Bell, CheckCircle2, AlertCircle, AlertTriangle, ShieldAlert, MessageSquare, CalendarCheck, Trophy } from 'lucide-react';

interface NotificationSettingsProps {
  userProfile: UserProfile;
  onUpdatePreferences: (newPrefs: UserNotificationPreferences) => Promise<void>;
  onToggleMasterPush: () => Promise<void>;
}

export function NotificationSettings({ userProfile, onUpdatePreferences, onToggleMasterPush }: NotificationSettingsProps) {
  const [saving, setSaving] = useState(false);
  const [localPrefs, setLocalPrefs] = useState<UserNotificationPreferences | null>(null);

  const normalizeMode = (val: any, defaultVal: NotificationMode): NotificationMode => {
    if (val === 'push_and_app' || val === 'app_only' || val === 'off') return val;
    if (val === true) return 'push_and_app';
    if (val === false) return 'off';
    return defaultVal;
  };

  const rawPrefs = userProfile.notificationPreferences || {};
  const prefs: UserNotificationPreferences = localPrefs || {
    social: normalizeMode((rawPrefs as any).social, 'push_and_app'),
    chat: normalizeMode((rawPrefs as any).chat, 'push_and_app'),
    tournaments: normalizeMode((rawPrefs as any).tournaments, 'push_and_app'),
    bookings: normalizeMode((rawPrefs as any).bookings, 'push_and_app'),
    marketing: normalizeMode((rawPrefs as any).marketing, 'app_only'),
    club_social: normalizeMode((rawPrefs as any).club_social, 'push_and_app'),
    club_chat: normalizeMode((rawPrefs as any).club_chat, 'push_and_app'),
    club_tournaments: normalizeMode((rawPrefs as any).club_tournaments, 'push_and_app'),
    club_bookings: normalizeMode((rawPrefs as any).club_bookings, 'push_and_app')
  };

  const isStaffOrAdmin = ['admin', 'co_admin', 'empleado', 'gestor_turnos', 'gestor_torneos', 'superadmin'].includes(userProfile.role);

  const handleModeChange = async (key: keyof UserNotificationPreferences, mode: NotificationMode) => {
    const newPrefs = { ...prefs, [key]: mode };
    setLocalPrefs(newPrefs);
    setSaving(true);
    try {
      await onUpdatePreferences(newPrefs);
    } finally {
      setSaving(false);
    }
  };

  const renderToggleGroup = (key: keyof UserNotificationPreferences, label: string, description: string, icon: React.ReactNode, locked: boolean = false) => {
    const currentMode = locked ? 'push_and_app' : prefs[key];

    return (
      <div className="bg-[#242730] border border-white/10 rounded-lg p-3 shadow-sm">
        <div className="flex items-start gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-yellow-400/10 flex items-center justify-center shrink-0 mt-0.5">
            {icon}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h4 className="text-[13px] font-bold text-white tracking-wide">{label}</h4>
              {locked && <span className="text-[8px] font-black uppercase bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded flex items-center gap-1 shrink-0"><ShieldAlert size={10} /> OBLIGATORIO</span>}
            </div>
            <p className="text-[11px] text-white/50 mt-0.5 leading-snug">{description}</p>
            
            <div className="mt-3 grid grid-cols-3 bg-black/30 rounded-lg overflow-hidden border border-white/5 relative z-10" style={{ WebkitTransform: 'translateZ(0)' }}>
              <button
                disabled={locked || saving}
                onClick={() => handleModeChange(key, 'off')}
                className={`py-2 px-1 text-[9px] font-black uppercase tracking-wider flex flex-col items-center justify-center gap-1 border-r border-white/5 ${
                  locked ? 'opacity-30 cursor-not-allowed' : ''
                } ${
                  currentMode === 'off' 
                    ? 'bg-white text-black' 
                    : 'text-white/60 hover:bg-white/5'
                }`}
              >
                <BellOff size={12} />
                <span>Off</span>
              </button>
              <button
                disabled={locked || saving}
                onClick={() => handleModeChange(key, 'app_only')}
                className={`py-2 px-1 text-[9px] font-black uppercase tracking-wider flex flex-col items-center justify-center gap-1 border-r border-white/5 ${
                  locked ? 'opacity-30 cursor-not-allowed' : ''
                } ${
                  currentMode === 'app_only' 
                    ? 'bg-yellow-400 text-black' 
                    : 'text-white/60 hover:bg-white/5'
                }`}
              >
                <Bell size={12} />
                <span>Solo App</span>
              </button>
              <button
                disabled={locked || saving}
                onClick={() => handleModeChange(key, 'push_and_app')}
                className={`py-2 px-1 text-[9px] font-black uppercase tracking-wider flex flex-col items-center justify-center gap-1 ${
                  locked ? 'opacity-50 cursor-not-allowed' : ''
                } ${
                  currentMode === 'push_and_app' 
                    ? 'bg-yellow-400 text-black' 
                    : 'text-white/60 hover:bg-white/5'
                }`}
              >
                <BellRing size={12} />
                <span>App+Push</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-2.5" style={{ WebkitTransform: 'translateZ(0)', transform: 'translateZ(0)' }}>
      
      {/* Master Push Switch */}
      <div className="bg-[#242730] border border-white/10 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-sm">
        <div>
          <h3 className="text-sm font-black uppercase tracking-widest text-white">Alertas Push Globales</h3>
          <p className="text-xs text-white/50 mt-1">Habilita o deshabilita la recepción de notificaciones en tu celular a nivel sistema.</p>
        </div>
        <button
          onClick={onToggleMasterPush}
          className={`relative w-14 h-7 rounded-full transition-all duration-300 shrink-0 ${userProfile?.notificationsEnabled
              ? 'bg-[#facc15] shadow-[0_0_12px_rgba(250,204,21,0.4)]'
              : 'bg-white/10'
            }`}
        >
          <div className={`absolute top-0.5 w-6 h-6 rounded-full transition-all duration-300 flex items-center justify-center ${userProfile?.notificationsEnabled
              ? 'left-[calc(100%-1.625rem)] bg-black shadow-md'
              : 'left-0.5 bg-white/30'
            }`}>
            {userProfile?.notificationsEnabled
              ? <BellRing size={12} className="text-[#facc15]" />
              : <BellOff size={12} className="text-white/50" />
            }
          </div>
        </button>
      </div>

      <div className="h-px bg-white/5 my-2" />

      {renderToggleGroup('social', 'Social y Comunidad', 'Menciones en posts, comentarios en tu muro y respuestas a tus interacciones.', <MessageSquare size={16} className="text-theme-primary" />)}
      
      {renderToggleGroup('chat', 'Mensajes Directos', 'Mensajes privados de otros jugadores o chats grupales.', <MessageSquare size={16} className="text-theme-primary" />)}
      
      {renderToggleGroup('tournaments', 'Torneos y Partidos', 'Avisos de inscripción a torneos, inicio de etapas y recordatorios de juego.', <Trophy size={16} className="text-theme-primary" />)}
      
      {renderToggleGroup('bookings', 'Turnos y Reservas', 'Confirmaciones de reservas en clubes, cancelaciones y propuestas duales.', <CalendarCheck size={16} className="text-theme-primary" />)}

      {isStaffOrAdmin && (
        <>
          <div className="h-px bg-white/5 my-4" />
          <div className="mb-2">
            <h3 className="text-sm font-black uppercase tracking-widest text-theme-primary">Alertas del Club (Staff)</h3>
            <p className="text-xs text-white/50 mt-1">Configura las notificaciones que recibes como administrador o empleado de la sede.</p>
          </div>
          {renderToggleGroup('club_bookings', 'Gestión de Turnos (Club)', 'Nuevas reservas, cancelaciones de jugadores y pagos recibidos.', <CalendarCheck size={16} className="text-theme-primary" />)}
          {renderToggleGroup('club_tournaments', 'Torneos y Validaciones (Club)', 'Inscripciones a torneos del club y resultados pendientes de validación.', <Trophy size={16} className="text-theme-primary" />)}
          {renderToggleGroup('club_chat', 'Mensajes al Club', 'Consultas directas a la cuenta del club.', <MessageSquare size={16} className="text-theme-primary" />)}
          {renderToggleGroup('club_social', 'Social del Club', 'Comentarios e interacciones en publicaciones de la sede.', <MessageSquare size={16} className="text-theme-primary" />)}
        </>
      )}

      {/* Critical/Locked Notifications Info Block */}
      <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0 mt-0.5">
            <AlertCircle size={16} className="text-blue-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-bold text-blue-400 tracking-wide">Avisos del Sistema</h4>
            <p className="text-xs text-white/60 mt-1 leading-relaxed">
              Las notificaciones esenciales (resultados, disputas, sanciones y validaciones) se mantendrán siempre activas para garantizar tu correcto funcionamiento dentro de la plataforma.
            </p>
          </div>
        </div>
      </div>

    </div>
  );
}
